import { displayWLoading } from '@pequity/shared/router/displayWLoading';
import { installLocaleFeaturesGuard, withLocaleFeatures } from '@pequity/shared/router/localeFeatures';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { aiAssistantRoutes } from '@/ai-assistant/routes';
import { approvalsRoutes } from '@/approvals/router/routes';
import { ccpRoutes as ccpv2Routes } from '@/ccpv2/router/routes';
import { dataRoutes } from '@/data/router/routes';
import { dataIntegrationsRoutes } from '@/data-integrations/router/routes';
import { documentAccessRoutes } from '@/document-access/router/routes';
import { fileCleanerRoutes } from '@/file-cleaner/router/routes';
import { homeRoutes } from '@/home/router/routes';
import { insightsRoutes } from '@/insights/router/routes';
import { jobArchitectureBuilderRoutes } from '@/job-architecture-builder/router/routes';
import { loginRoutes } from '@/login/router/routes';
import { marketDataRoutes } from '@/market-data/router/routes';
import { marketPulseRoutes } from '@/market-pulse/router/routes';
import { onboardingRoutes } from '@/onboarding/router/routes';
import { peopleRoutes } from '@/people/router/routes';
import { playgroundRoutes } from '@/playground/router/routes';
import { ensureFeatures } from '@/plugins/i18n';
import { rangesRoutes } from '@/ranges/router/routes';
import {
  handleFirstLogin,
  handleHelpWidgetClosing,
  handleNonLoggedInUsers,
  handleNonPublicRoutes,
  handlePBELogin,
  handleTempPasswordChange,
  loadAppConfig,
  loggedInActions,
  redirectSubdomainToBaseUrl,
  removeSplashLoader,
  savePBELoginFlag,
} from '@/router/guards';
import { handleChunkLoadError } from '@/router/handleChunkLoadError';
import { settingsRoutes } from '@/settings/router/routes';

// Route components dynamic imports
const PageNotFound = () => import('@/pages/not-found.vue');

// Default layout
const DEFAULT_LAYOUT = 'layout-default';

const history = createWebHistory('/v3/');

// Standalone-only feature locales each module needs; everything else is core (see
// tmp/plans/locale-feature-bundles.md). Modules not listed here reach only core strings.
const routes = [
  ...withLocaleFeatures(loginRoutes, ['login']),
  ...withLocaleFeatures(onboardingRoutes, ['onboarding', 'login']),
  ...withLocaleFeatures(homeRoutes, ['free_trial_onboarding', 'ccp', 'data', 'settings']),
  ...withLocaleFeatures(rangesRoutes, ['ranges', 'data', 'self_serve_columns']),
  ...withLocaleFeatures(aiAssistantRoutes, ['ai_assistant']),
  ...insightsRoutes,
  ...withLocaleFeatures(dataRoutes, ['data', 'self_serve_columns']),
  ...withLocaleFeatures(peopleRoutes, ['people', 'data', 'self_serve_columns', 'ranges', 'settings']),
  ...withLocaleFeatures(approvalsRoutes, ['approvals']),
  ...withLocaleFeatures(ccpv2Routes, ['ccp', 'data', 'settings']),
  ...withLocaleFeatures(marketPulseRoutes, ['market']),
  ...withLocaleFeatures(marketDataRoutes, ['market', 'data', 'self_serve_columns']),
  ...withLocaleFeatures(settingsRoutes, ['settings', 'people']),
  ...withLocaleFeatures(dataIntegrationsRoutes, ['data']),
  ...withLocaleFeatures(fileCleanerRoutes, ['file_cleaner']),
  ...withLocaleFeatures(jobArchitectureBuilderRoutes, ['job_architecture_builder']),
  ...withLocaleFeatures(playgroundRoutes, ['ccp', 'ai_assistant', 'self_serve_columns']),
  ...documentAccessRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: PageNotFound,
  },
].map((route: RouteRecordRaw) => {
  // Set default layout for all routes to avoid flash of content on the welcome back page
  route.meta = route.meta?.layout ? route.meta : { ...route.meta, layout: DEFAULT_LAYOUT };

  return route;
});

const router = createRouter({
  linkActiveClass: 'active',
  history,
  routes,
});

// Global guards (called in order) - https://router.vuejs.org/guide/advanced/navigation-guards.html#Global-Before-Guards
[
  loadAppConfig,
  redirectSubdomainToBaseUrl,
  savePBELoginFlag,
  handleNonPublicRoutes,
  handleHelpWidgetClosing,
  handleNonLoggedInUsers,
  handleFirstLogin,
  handleTempPasswordChange,
  handlePBELogin,
  loggedInActions,
  removeSplashLoader,
].forEach((guard) => {
  router.beforeEach(guard);
});

installLocaleFeaturesGuard(router, ensureFeatures);
handleChunkLoadError(router);
displayWLoading(router);

export { router };
